import axios from 'axios';

export const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (window.location.hostname === 'localhost' ? 'http://localhost:8000' : window.location.origin);

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export type SortDir = 'asc' | 'desc';

export function sortBy<T>(rows: T[], key: string, dir: SortDir): T[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = (a as Record<string, unknown>)[key];
    const bv = (b as Record<string, unknown>)[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}

export interface ErpCatalogMatch {
  pp_number?: string | null;
  vendor_item_no?: string | null;
  description?: string | null;
  manufacturer?: string | null;
  manufacturer_code?: string | null;
  vendor_code?: string | null;
  customer_part_number?: string | null;
  description_2?: string | null;
  uom?: string | null;
  rohs?: string | null;
  reach?: string | null;
  ul_file?: string | null;
  ul_ccn?: string | null;
  match?: 'exact' | 'near';
  score?: number;
}

export interface ImprovementHint {
  value?: string | null;
  label?: string | null;
  source?: string;
  missing?: boolean;
  current?: string | null;
  outcome?: 'add' | 'amend' | 'append' | 'flag' | string;
  risk?: boolean;
  match_type?: string | null;
  over_limit?: boolean;
  max_length?: number;
}

export interface FieldDecision {
  field: string;
  bc?: string;
  value?: string;
  outcome?: string;
  user?: string;
  at?: string;
}

export interface AmendErpResult {
  written: Array<{ item: string; line_id: number; fields: string[] }>;
  stale: Array<{ item: string; line_id: number; vendor?: string; changes: Array<{ field: string; approved_against: string; now: string }> }>;
  blocked: Array<{ item: string; line_id: number; vendor?: string; now?: string; reason: string }>;
  errors: Array<{ item: string; line_id: number; detail: string }>;
}

export interface LineImprovements {
  manufacturer?: ImprovementHint;
  description?: ImprovementHint;
  customer_part_number?: ImprovementHint;
  rohs?: ImprovementHint;
  reach?: ImprovementHint;
  ul_file?: ImprovementHint;
  ul_ccn?: ImprovementHint;
}

export interface MountingMethod {
  id: number | null;
  key: string;
  label: string;
  seconds: number;
  seeded: boolean;
  no_connections?: boolean;
}

export interface ImportSkip {
  row: number;
  reason: string;
}

export interface Quote {
  id: string;
  customer: string | null;
  quote_ref: string;
  version: number;
  status: string;
  cost_centre: string | null;
  enclosure_h: number | null;
  enclosure_w: number | null;
  enclosure_d: number | null;
  cable_type: string;
  mounting_locations: string[];
  manufactured_in_batch: boolean;
  pre_drilled: boolean;
  additional_idents: boolean;
  notes: string | null;
  source: string;
  source_job_id: string | null;
  enhancement_complete: boolean;
  labour?: LabourResult | null;
  project?: Record<string, any>;
  parent_quote_id?: string | null;
  created_by: string | null;
  updated_by: string | null;
  volume_factor?: number | null;
  finalised_by: string | null;
  finalised_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  line_count: number;
  labour_ready?: boolean;
  labour_gaps?: {
    quote: string[];
    lines: { mounting_method: number; mounting_location: number; connections: number };
    ready: boolean;
  };
}

export interface QuoteLine {
  id: number;
  quote_id: string;
  recon_match_id: number | null;
  tag: string | null;
  ref: string | null;
  tag_origin: string;
  type_category: string | null;
  erp_number: string | null;
  customer_part_number: string | null;
  vendor_item_no: string | null;
  description: string | null;
  qty: number;
  manufacturer: string | null;
  enrich_status: string;
  crawl_json: Record<string, unknown> | null;
  crawl_confidence: number | null;
  connections: number | null;
  mounting_method: string | null;
  mounting_location: string | null;
  flags: string[];
  labour_hrs: number | null;
  labour_confidence: number | null;
  labour_inputs: Record<string, any>;
  labour_method_a: number | null;
  labour_method_b: number | null;
  historical_hrs: number | null;
  is_deleted: boolean;
  quality_flags?: string[];
  erp_catalog?: ErpCatalogMatch | null;
  erp_extras?: Record<string, any> | null;
  improvements?: LineImprovements | null;
}

export interface SourceJob {
  job_id: string;
  pdf_filename: string;
  customer: string | null;
  description?: string | null;
  status: string;
  confirmed_count: number;
  created_by?: string | null;
  created_at: string | null;
  updated_at?: string | null;
}

export interface CostCentre {
  code: string;
  name: string;
}

export interface LabourParam {
  id: number;
  category: string;
  key: string;
  value: number;
  unit: string;
  description: string | null;
}

export interface LabourRow {
  label: string;
  count?: number;
  qty?: number;
  holes?: number;
  minutes: number;
  seconds?: number;
  seconds_per_hole?: number;
  holes_per_device?: number;
  wiring_kind?: string;
  method?: number;
  short_auto_sec?: number;
  auto_sec?: number;
  manual_sec?: number;
}

export interface LabourResult {
  mechanical_hrs: number;
  electrical_hrs: number;
  drilling_hrs: number;
  project_hrs: number;
  base_hrs: number;
  test_hrs: number;
  material_mgmt_hrs: number;
  hb_hrs: number;
  after_allowances_hrs: number;
  final_hrs: number;
  batch_applied: boolean;
  batch_factor?: number;
  volume_factor?: number;
  drilling_method?: number;
  wiring_kind?: string;
  test_pct?: number;
  material_mgmt_pct?: number;
  hb_pct?: number;
  mechanical_minutes?: number;
  electrical_minutes?: number;
  drilling_minutes?: number;
  project_minutes?: number;
  mechanical_rows: LabourRow[];
  electrical_rows: LabourRow[];
  drilling_rows: LabourRow[];
  project_rows: LabourRow[];
  lines: Array<Record<string, unknown>>;
  low_confidence_count: number;
  flagged_count: number;
  assembly_confidence?: number | null;
  labour_gaps?: Quote['labour_gaps'];
  quote?: Quote;
}

export interface FlagCategory {
  id: number;
  key: string;
  label: string;
}

export interface SchematicMeta {
  tag: string | null;
  pages: number[];
  all_pages?: number[];
  page_count?: number;
  available: boolean;
  pdf_filename: string | null;
  reason: string | null;
  popout_path?: string;
}

export interface ErpCodeOption {
  code: string;
  name: string;
}

export interface ErpPreview {
  line_id: number;
  vendor_item_no: string;
  description: string;
  description2: string;
  customerPartNumber: string;
  replenishmentSystem: string;
  unitCost: number;
  manufacturerCode: string;
  manufacturerEntered: string;
  itemCategoryCode: string;
  commodityCode: string;
  conflictMineralsStatus: string;
  countryOfOriginCode: string;
  costCenter: string;
  baseUnitOfMeasureCode: string;
  rohsCompliant: string;
  reachCompliance: string;
  ukcaCe: string;
  ulFileNo: string;
  ulCcnNo: string;
  ulMissing: boolean;
  qty: number;
  ulNote: string;
  warnings: string[];
  manufacturers: ErpCodeOption[];
  itemCategories: ErpCodeOption[];
  commodities: ErpCodeOption[];
  costCenters: ErpCodeOption[];
  countries: ErpCodeOption[];
  crawl_confidence?: number | null;
  is_new: boolean;
}

export const searchCatalog = async (query: string): Promise<ErpCatalogMatch[]> => {
  const { data } = await api.get('/api/quotes/catalog-search', { params: { q: query } });
  return data.matches || [];
};

export const previewErp = async (quoteId: string, lineId: number): Promise<ErpPreview> => {
  const { data } = await api.get(`/api/quotes/${quoteId}/lines/${lineId}/erp-preview`);
  return data.preview;
};

export const lookupCatalog = async (pp?: string, vendor?: string): Promise<ErpCatalogMatch> => {
  const { data } = await api.get('/api/quotes/catalog', { params: { pp: pp || undefined, vendor: vendor || undefined } });
  return data.item;
};

export const listQuotes = async (): Promise<Quote[]> => {
  const { data } = await api.get('/api/quotes');
  return data.quotes;
};

export function displayTagRef(line: Pick<QuoteLine, 'tag' | 'ref' | 'erp_number' | 'vendor_item_no'>): string {
  const keys = new Set(
    [line.erp_number, line.vendor_item_no].filter((value): value is string => Boolean(value) && value !== 'NEW ITEM')
  );
  for (const value of [line.tag, line.ref]) {
    if (value && !keys.has(value)) return value;
  }
  return '';
}

export const listSourceJobs = async (): Promise<SourceJob[]> => {
  const { data } = await api.get('/api/quotes/source-jobs');
  return data.jobs;
};

export const listCostCentres = async (): Promise<CostCentre[]> => {
  const { data } = await api.get('/api/quotes/cost-centres');
  return data.cost_centres;
};

export const createQuote = async (payload: {
  job_id?: string;
  cost_centre: string;
  customer?: string;
  source?: string;
  rows?: Record<string, unknown>[];
  enclosure_h?: number;
  enclosure_w?: number;
  enclosure_d?: number;
  cable_type?: string;
  notes?: string;
  mounting_locations?: string[];
  volume_factor?: number;
  pre_drilled?: boolean;
  additional_idents?: boolean;
}): Promise<Quote> => {
  const { data } = await api.post('/api/quotes', payload);
  return data.quote;
};

export const getQuote = async (id: string): Promise<Quote> => {
  const { data } = await api.get(`/api/quotes/${id}`);
  return data.quote;
};

export const updateQuote = async (id: string, fields: Partial<Quote> & { project_json?: Record<string, unknown> }): Promise<Quote> => {
  const { data } = await api.patch(`/api/quotes/${id}`, fields);
  return data.quote;
};

export const deleteQuote = async (id: string): Promise<void> => {
  await api.delete(`/api/quotes/${id}`);
};

export const copyQuote = async (id: string): Promise<Quote> => {
  const { data } = await api.post(`/api/quotes/${id}/copy`);
  return data.quote;
};

export const listLines = async (quoteId: string): Promise<QuoteLine[]> => {
  const { data } = await api.get(`/api/quotes/${quoteId}/lines`);
  return data.lines;
};

export const addLine = async (quoteId: string, fields: Partial<QuoteLine> & { use_catalog?: boolean }): Promise<QuoteLine> => {
  const { data } = await api.post(`/api/quotes/${quoteId}/lines`, fields);
  return data.line;
};

export const updateLine = async (
  quoteId: string,
  lineId: number,
  fields: Partial<QuoteLine>
): Promise<QuoteLine> => {
  const { data } = await api.patch(`/api/quotes/${quoteId}/lines/${lineId}`, fields);
  return data.line;
};

export const updateLinesBulk = async (
  quoteId: string,
  lineIds: number[],
  fields: { mounting_method?: string; mounting_location?: string; connections?: number; no_wire?: boolean }
): Promise<QuoteLine[]> => {
  const { data } = await api.patch(`/api/quotes/${quoteId}/lines/bulk`, { line_ids: lineIds, ...fields });
  return data.lines;
};

export const deleteLine = async (quoteId: string, lineId: number): Promise<QuoteLine> => {
  const { data } = await api.post(`/api/quotes/${quoteId}/lines/${lineId}/delete`);
  return data.line;
};

export const restoreLine = async (quoteId: string, lineId: number): Promise<QuoteLine> => {
  const { data } = await api.post(`/api/quotes/${quoteId}/lines/${lineId}/restore`);
  return data.line;
};

export const crawlAll = async (quoteId: string): Promise<QuoteLine[]> => {
  const { data } = await api.post(`/api/quotes/${quoteId}/crawl`);
  return data.lines;
};

export const crawlLine = async (quoteId: string, lineId: number): Promise<QuoteLine> => {
  const { data } = await api.post(`/api/quotes/${quoteId}/lines/${lineId}/crawl`);
  return data.line;
};

export const amendErp = async (quoteId: string, continueStale = false): Promise<AmendErpResult> => {
  const { data } = await api.post(`/api/quotes/${quoteId}/amend-erp`, { continue_stale: continueStale });
  return data;
};

export interface VendorChange {
  line_id: number;
  tag: string;
  erp_number: string;
  previous_vendor_item: string;
  vendor_item: string;
  previous_vendor_no: string;
  vendor_no: string;
  previous_description: string;
  description: string;
  vendor_item_changed: boolean;
  vendor_no_changed: boolean;
}

export interface ErpSyncResult {
  lines_updated: number;
  lines_unchanged: number;
  lines_missing: number;
  catalog_fetched: number;
  catalog_created: number;
  catalog_updated: number;
  window: { from: string; to: string; complete: boolean };
  vendor_changes: VendorChange[];
}

export const syncErp = async (quoteId: string): Promise<ErpSyncResult> => {
  const { data } = await api.post(`/api/quotes/${quoteId}/erp-sync`);
  return data;
};

export const writeErp = async (quoteId: string, lineId: number, payload: Record<string, unknown>): Promise<QuoteLine & { matching_lines_updated?: number }> => {
  const { data } = await api.post(`/api/quotes/${quoteId}/lines/${lineId}/erp`, payload);
  return { ...data.line, matching_lines_updated: data.matching_lines_updated || 0 };
};

export const getSchematicMeta = async (quoteId: string, lineId: number): Promise<SchematicMeta> => {
  const { data } = await api.get(`/api/quotes/${quoteId}/lines/${lineId}/schematic`);
  return data;
};

export const schematicImageUrl = (quoteId: string, lineId: number, page?: number) =>
  `${API_BASE}/api/quotes/${quoteId}/lines/${lineId}/schematic.png${page ? `?page=${page}` : ''}`;

export const markEnhancementComplete = async (quoteId: string): Promise<Quote> => {
  const { data } = await api.post(`/api/quotes/${quoteId}/enhancement-complete`);
  return data.quote;
};

export const calculateLabour = async (quoteId: string): Promise<LabourResult> => {
  const { data } = await api.post(`/api/quotes/${quoteId}/labour`);
  return data;
};

export const finaliseQuote = async (quoteId: string): Promise<Quote> => {
  const { data } = await api.post(`/api/quotes/${quoteId}/finalise`);
  return data.quote;
};

export const listAudit = async (quoteId: string): Promise<Array<{ event_type: string; detail: string; created_by: string | null; created_at: string | null }>> => {
  const { data } = await api.get(`/api/quotes/${quoteId}/audit`);
  return data.events;
};

export const exportUrl = (quoteId: string, kind: 'bom' | 'labour') =>
  `${API_BASE}/api/quotes/${quoteId}/export/${kind}`;

export const importTemplateUrl = () => `${API_BASE}/api/quotes/import-template`;

export const importExcel = async (
  quoteId: string,
  file: File
): Promise<{ added: number; skipped: number | ImportSkip[] }> => {
  const form = new FormData();
  form.append('file', file);
  const token = localStorage.getItem('auth_token');
  const { data } = await axios.post(`${API_BASE}/api/quotes/${quoteId}/import-excel`, form, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  return data;
};

export const listLabourParams = async (): Promise<LabourParam[]> => {
  const { data } = await api.get('/api/quotes/labour-params');
  return data.params;
};

export const updateLabourParam = async (
  paramId: number,
  value?: number,
  extra?: { no_connections?: boolean }
): Promise<LabourParam> => {
  const { data } = await api.patch(`/api/quotes/labour-params/${paramId}`, {
    ...(value == null ? {} : { value }),
    ...(extra || {}),
  });
  return data.param;
};

export const listFlagCategories = async (): Promise<FlagCategory[]> => {
  const { data } = await api.get('/api/quotes/flag-categories');
  return data.flags;
};

export const addFlagCategory = async (label: string): Promise<FlagCategory> => {
  const { data } = await api.post('/api/quotes/flag-categories', { label });
  return data.flag;
};

export const deleteFlagCategory = async (id: number): Promise<void> => {
  await api.delete(`/api/quotes/flag-categories/${id}`);
};

export const listMountingMethods = async (): Promise<MountingMethod[]> => {
  const { data } = await api.get('/api/quotes/mounting-methods');
  return data.methods;
};

export const addMountingMethod = async (
  name: string,
  seconds: number,
  noConnections = false
): Promise<MountingMethod> => {
  const { data } = await api.post('/api/quotes/mounting-methods', { name, seconds, no_connections: noConnections });
  return data.method;
};

export const deleteMountingMethod = async (id: number): Promise<void> => {
  await api.delete(`/api/quotes/mounting-methods/${id}`);
};
